import styled, { css } from "styled-components";
import { boxShadows, corners, media, typography } from "../../styles";
import { applyOpacity } from "../../styles/utils";
export type ButtonVariant = "destructive" | "primary" | "secondary" | "tertiary";
const DEFAULT_VARIANT = "primary";
const largeButton = css`
  min-height: 2.5em;
  ${typography.bodyBold}
`;
const smallButton = css`
  min-height: 2.25em;
  ${typography.smallBold};

  &:active,
  &:disabled,
  &:focus {
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05)};
  }

  @media ${media.hoverable} {
    &:hover {
      background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05)};
    }
  }
`;
const BaseButton = styled.button`
  display: block;
  min-width: 0.75rem;

  border: 0;
  ${corners.m}
  &::first-letter {
    text-transform: capitalize;
  }

  &:hover {
    cursor: ${({
  disabled
}) => disabled ? "not-allowed" : "pointer"};
  }

  &:focus {
    outline: none;
  }
`;
const primary = css`
  ${largeButton}
  background-color: ${({
  theme
}) => theme.colours.primary};
  color: ${({
  theme
}) => theme.colours.textOnPrimary};

  &:active:not(:disabled) {
    color: ${({
  theme
}) => applyOpacity(theme.colours.textOnPrimary, 0.8, false)};
    border: 0;
  }

  &:disabled {
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, theme.colours.isDark.primaryBackground ? 0.4 : 0.1)};
    color: ${({
  theme
}) => applyOpacity(theme.colours.textOnPrimary, theme.colours.isDark.primaryBackground ? 0.9 : 0.5)};
  }

  @media ${media.hoverable} {
    &:hover:not(:disabled) {
      background-color: ${({
  theme
}) => applyOpacity(theme.colours.primary, theme.colours.isDark.primaryBackground ? 0.9 : 0.7)};
    }
  }

  &:focus {
    ${boxShadows.whiteInsetBorderShadow}
    outline: 1px solid ${({
  theme
}) => theme.colours.primary};
  }
`;
const secondary = css`
  ${largeButton}
  border: ${({
  theme
}) => theme.colours.secondary} 2px solid;
  background-color: ${({
  theme
}) => theme.colours.white};
  color: ${({
  theme
}) => theme.colours.secondaryText};

  &:active:not(:disabled) {
    background-color: ${({
  theme
}) => theme.colours.white};
    color: ${({
  theme
}) => applyOpacity(theme.colours.secondaryText, 0.8)};
  }

  &:disabled {
    border: ${({
  theme
}) => theme.colours.isDark.secondaryForeground ? applyOpacity(theme.colours.dark, 0.4) + " 2px solid" : 0};
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, theme.colours.isDark.secondaryForeground ? 0 : 0.05)};
    color: ${({
  theme
}) => applyOpacity(theme.colours.dark, theme.colours.isDark.secondaryForeground ? 0.5 : 0.4)};
  }

  @media ${media.hoverable} {
    &:hover:not(:disabled) {
      border: ${({
  theme
}) => applyOpacity(theme.colours.secondary, 0.5) + " 2px solid"};
    }
  }

  &:focus {
    ${boxShadows.whiteInsetBorderShadow}
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.secondaryText, theme.colours.isDark.secondaryForeground ? 0.1 : 0.05)};
  }
`;
const tertiary = css<{
  listItem?: boolean;
}>`
  ${smallButton}
  border: ${({
  listItem,
  theme
}) => listItem ? 0 : applyOpacity(theme.colours.dark, 0.05)} solid 2px;
  background-color: ${({
  theme
}) => theme.colours.white};

  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.8)};

  &:disabled {
    border: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05)} solid 2px;
  }

  @media ${media.hoverable} {
    &:hover {
      border: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05)} solid 2px;
    }
  }

  &:active:not(:disabled) {
    border: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.2)} solid 2px;
    color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  }

  &:disabled {
    color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.5)};
  }

  &:focus {
    border: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)} solid 2px;
  }
`;
const destructive = css`
  ${smallButton}
  background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.02)};
  color: ${({
  theme
}) => theme.colours.error};

  &:active:not(:disabled) {
    outline: 1px solid ${({
  theme
}) => theme.colours.error};
  }

  &:disabled {
    color: ${({
  theme
}) => applyOpacity(theme.colours.error, 0.5)};
  }

  &:focus {
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.04)};
    outline: 1px solid ${({
  theme
}) => theme.colours.error};
  }
`;
const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  destructive,
  primary,
  secondary,
  tertiary
};
export const Button = styled(BaseButton)<{
  disabled?: boolean;
  listItem?: boolean;
  variant?: ButtonVariant;
}>`
  ${({
  variant
}) => variantStyles[variant ?? DEFAULT_VARIANT]}
`;
export default Button;